import { useState, useEffect } from 'react';
import archetypeService from '../helpers/archetypeService';

export const useArchetypeQuiz = () => {
    const [questions, setQuestions] = useState([]);
    const [responses, setResponses] = useState({});
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [loading, setLoading] = useState(true); // Cambiar a true para mostrar loading inicial
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    // Cargar preguntas al inicializar
    useEffect(() => {
        const loadQuestions = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await archetypeService.getQuestions();
                if (response.success && response.questions) {
                    setQuestions(response.questions);
                } else {
                    throw new Error('No se pudieron cargar las preguntas');
                }
            } catch (err) {
                console.error('Error al cargar las preguntas:', err.message);
                setError('Error al cargar las preguntas del cuestionario');
            } finally {
                setLoading(false);
            }
        };

        loadQuestions();
    }, []);

    // Manejar respuesta a una pregunta
    const handleAnswer = (questionId, answer) => {
        console.log('Adding response:', { questionId, answer });
        setResponses(prev => {
            const newResponses = {
                ...prev,
                [questionId]: answer
            };
            console.log('Updated responses:', newResponses);
            return newResponses;
        });
    };

    // Ir a la siguiente pregunta
    const nextQuestion = () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        }
    };

    // Ir a la pregunta anterior
    const previousQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
        }
    };

    // Ir a una pregunta específica
    const goToQuestion = (index) => {
        if (index >= 0 && index < questions.length) {
            setCurrentQuestionIndex(index);
        }
    };

    // Mapeo de preguntas del backend a las preguntas exactas que espera el submit
    const questionMapping = {
        "¿Cuál es tu nivel de digitalización actual?": "¿Cuál es tu nivel de digitalización actual?",
        "¿Quién lidera la estrategia comercial y de marketing?": "¿Quién lidera la estrategia comercial y de marketing?",
        "¿Cuál es tu prioridad principal hoy?": "¿Cuál es tu prioridad principal hoy?",
        "¿Tienes campañas activas o programadas con frecuencia?": "¿Tienes campañas activas o programadas con frecuencia?",
        "¿Qué canales usas para interactuar con tus clientes?": "¿Qué canales usas para interactuar con tus clientes?",
        "¿Cómo gestionas tu base de clientes?": "¿Cómo gestionas tu base de clientes?",
        "¿Qué tono comunicacional prefieres para tu marca?": "¿Qué tono comunicacional prefieres para tu marca?",
        "¿Tienes definido un plan de marketing o estrategia anual?": "¿Tienes definido un plan de marketing o estrategia anual?",
        "¿Qué tan cómodo/a te sientes usando herramientas digitales nuevas?": "¿Qué tan cómodo/a te sientes usando herramientas digitales nuevas?",
        "¿Tienes un equipo que pueda ejecutar acciones de marketing?": "¿Tienes un equipo que pueda ejecutar acciones de marketing?",
        "¿Mides resultados como CAC, LTV, tasa de recompra, etc.?": "¿Mides resultados como CAC, LTV, tasa de recompra, etc.?",
        "¿Qué tan importante es el marketing en tu negocio?": "¿Qué tan importante es el marketing en tu negocio?"
    };

    // Transformar respuestas de ID a texto de pregunta
    const transformResponsesForBackend = (responses, questions) => {
        const transformedResponses = {};

        // Usar el mapeo para asegurar que las claves coincidan exactamente
        questions.forEach(question => {
            const questionId = question.id;
            if (responses[questionId] !== undefined) {
                const expectedQuestion = questionMapping[question.question];
                if (expectedQuestion) {
                    transformedResponses[expectedQuestion] = responses[questionId];
                } else {
                    console.warn('Question not found in mapping:', question.question);
                    transformedResponses[question.question] = responses[questionId];
                }
            }
        });

        return transformedResponses;
    };

    // Verificar que todas las preguntas estén contestadas
    const verifyAllQuestionsAnswered = (responses, questions) => {
        const unansweredQuestions = [];

        questions.forEach(question => {
            if (responses[question.id] === undefined) {
                unansweredQuestions.push({
                    id: question.id,
                    question: question.question
                });
            }
        });

        if (unansweredQuestions.length > 0) {
            console.warn('Unanswered questions:', unansweredQuestions);
            return {
                allAnswered: false,
                unansweredQuestions
            };
        }

        return {
            allAnswered: true,
            unansweredQuestions: []
        };
    };

    // Enviar cuestionario
    const submitQuiz = async (customResponses = null) => {
        setSubmitting(true);
        setError(null);

        // Usar las respuestas personalizadas si se proporcionan, sino usar el estado
        const responsesToUse = customResponses || responses;

        // Verificar que todas las preguntas estén contestadas
        if (process.env.NODE_ENV === 'development') {
            console.log('Responses to use:', responsesToUse);
            console.log('Questions:', questions.map(q => ({ id: q.id, question: q.question })));
        }

        const verification = verifyAllQuestionsAnswered(responsesToUse, questions);
        if (!verification.allAnswered) {
            const errorMessage = `Faltan ${verification.unansweredQuestions.length} pregunta(s) por contestar. Por favor, completa todas las preguntas antes de enviar.`;
            console.error(errorMessage, verification.unansweredQuestions);
            setError(errorMessage);
            setSubmitting(false);
            throw new Error(errorMessage);
        }

        // Transformar respuestas al formato que espera el backend
        const transformedResponses = transformResponsesForBackend(responsesToUse, questions);

        try {
            const response = await archetypeService.submitQuiz(transformedResponses);
            if (response.success) {
                setResult(response.result);
                return response.result;
            } else {
                throw new Error(response.message || 'Error al procesar el cuestionario');
            }
        } catch (err) {
            console.error('Error al procesar el cuestionario:', err.message);
            setError('Error al procesar el cuestionario');
            throw err;
        } finally {
            setSubmitting(false);
        }
    };

    // Reiniciar cuestionario
    const resetQuiz = () => {
        setResponses({});
        setCurrentQuestionIndex(0);
        setResult(null);
        setError(null);
    };

    // Calcular progreso
    const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;

    // Verificar si todas las preguntas están respondidas
    const isComplete = questions.length > 0 && questions.every(question => responses[question.id] !== undefined);

    // Obtener pregunta actual
    const currentQuestion = questions && questions.length > 0 && currentQuestionIndex >= 0 && currentQuestionIndex < questions.length
        ? questions[currentQuestionIndex]
        : null;

    // Obtener preguntas no contestadas
    const getUnansweredQuestions = () => {
        return questions.filter(question => responses[question.id] === undefined);
    };

    return {
        // Estado
        questions,
        responses,
        currentQuestionIndex,
        currentQuestion,
        loading,
        submitting,
        error,
        result,
        progress,
        isComplete,

        // Acciones
        handleAnswer,
        nextQuestion,
        previousQuestion,
        goToQuestion,
        submitQuiz,
        resetQuiz,
        setError,

        // Utilidades
        getUnansweredQuestions,
        verifyAllQuestionsAnswered
    };
}; 